import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { Arrow, Caret, Close } from '../icons'
import { Title4, BodyLarge } from '../styles/Typography'
import { Stack } from '@mui/material'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { isTabletOrMobile } from '../../utils/devices'
import { useNavigate } from 'react-router-dom'
import { useScreeningQuestionnaireStore } from '../../state/store'
import LinearProgress from '@mui/material/LinearProgress'
import { useTranslation } from 'react-i18next'

interface Props {
  goBack?: (() => void) | false
  disableBack?: boolean
  title: string
  progress?: number
  date?: string | null
  closePath?: string
}

export const ScreeningHeader = ({
  title,
  goBack,
  disableBack = false,
  progress,
  date,
  closePath = '/portal/health-services/screening',
}: Props) => {
  const { t } = useTranslation()
  const width = useWindowWidth()
  const navigate = useNavigate()
  const { resetState } = useScreeningQuestionnaireStore()
  const ArrowBackIcon = isTabletOrMobile(width) ? Caret : Arrow
  const iconSize = isTabletOrMobile(width) ? 24 : 32

  const close = () => {
    resetState()
    navigate(closePath)
  }
  const progressBarHeight = isTabletOrMobile(width) ? 4 : 6
  return (
    <HeaderContainer>
      {progress !== undefined && (
        <ProgressBarContainer $isMobile={isTabletOrMobile(width)}>
          <LinearProgress variant="determinate" value={progress} color="primary" sx={{ height: progressBarHeight }} />
        </ProgressBarContainer>
      )}

      <Section flexDirection="row" justifyContent="space-between">
        {goBack ? (
          <BackArrowWrapper disabled={disableBack} onClick={disableBack ? undefined : goBack}>
            <ArrowBackIcon left size={iconSize} color={disableBack ? theme.color.grey3 : theme.color.plum} />
          </BackArrowWrapper>
        ) : (
          <div style={{ height: iconSize, width: iconSize }} />
        )}
        <TitleContainer>
          <Title4>{title}</Title4>
          {date && <BodyLarge>{t('employee.screening.basedOnDate', { date })}</BodyLarge>}
        </TitleContainer>
        <Close size={iconSize} onClick={close} color={theme.color.plum} />
      </Section>
    </HeaderContainer>
  )
}

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: ${theme.spacing.small}px;
`
const HeaderContainer = styled.div`
  position: relative;
  padding: ${theme.spacing.large}px ${theme.spacing.medium}px ${theme.spacing.medium}px;

  & svg {
    cursor: pointer;
  }
  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    padding-bottom: ${theme.spacing.large}px;
    border-bottom: 1px solid ${theme.color.beigeDark};
  }

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    padding-bottom: ${theme.spacing.large}px;
  }
`

const ProgressBarContainer = styled.div<{ $isMobile: boolean }>`
  position: absolute;
  ${({ $isMobile }) => ($isMobile ? 'top: 0;' : 'bottom: 0;')}
  left: 0;
  width: 100%;
  padding: 0;
  margin: 0;
`

const Section = styled(Stack)<{ $progress?: number }>`
  max-width: 600px;
  ${({ $progress }) => $progress !== undefined && 'min-height: 40px;'}
  margin: 0 auto;
  text-align: center;
  height: 100%;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    min-height: 20px;
  }
`

const BackArrowWrapper = styled.div<{ disabled?: boolean }>`
  && svg {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  }
`
